import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { 
  getAdminMetrics, 
  getAgentApplications, 
  getActiveAgents, 
  getAllUsers 
} from '../utils/api';
import toast from '../utils/toastUtils';

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_ADMIN_METRICS: 'SET_ADMIN_METRICS',
  SET_AGENT_APPLICATIONS: 'SET_AGENT_APPLICATIONS',
  SET_ACTIVE_AGENTS: 'SET_ACTIVE_AGENTS',
  SET_USERS: 'SET_USERS',
  INVALIDATE_CACHE: 'INVALIDATE_CACHE',
  INVALIDATE_AGENT_CACHE: 'INVALIDATE_AGENT_CACHE',
  INVALIDATE_USER_CACHE: 'INVALIDATE_USER_CACHE',
};

// Initial state
const initialState = {
  adminMetrics: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
    isStale: true,
  },
  agentApplications: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
    isStale: true,
  },
  activeAgents: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
    isStale: true,
  },
  users: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
    isStale: true,
    pagination: {
      page: 1,
      limit: 50,
      totalPages: 1,
    },
    sortBy: 'date',
    searchQuery: '',
  },
};

// Reducer
const adminDataReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        [action.dataType]: {
          ...state[action.dataType],
          loading: action.loading,
          error: action.loading ? null : state[action.dataType].error,
        },
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        [action.dataType]: {
          ...state[action.dataType],
          loading: false,
          error: action.error,
        },
      };

    case ACTIONS.SET_ADMIN_METRICS:
      return {
        ...state,
        adminMetrics: {
          data: action.data,
          loading: false,
          error: null,
          lastFetched: Date.now(),
          isStale: false,
        },
      };

    case ACTIONS.SET_AGENT_APPLICATIONS:
      return {
        ...state,
        agentApplications: {
          data: action.data,
          loading: false,
          error: null,
          lastFetched: Date.now(),
          isStale: false,
        },
      };

    case ACTIONS.SET_ACTIVE_AGENTS:
      return {
        ...state,
        activeAgents: {
          data: action.data,
          loading: false,
          error: null,
          lastFetched: Date.now(),
          isStale: false,
        },
      };

    case ACTIONS.SET_USERS:
      return {
        ...state,
        users: {
          ...state.users,
          data: action.data.users,
          pagination: {
            page: action.data.page,
            limit: action.data.limit,
            totalPages: action.data.totalPages,
          },
          loading: false,
          error: null,
          lastFetched: Date.now(),
          isStale: false,
          sortBy: action.sortBy || state.users.sortBy,
          searchQuery: action.searchQuery || state.users.searchQuery,
        },
      };

    case ACTIONS.INVALIDATE_CACHE:
      return {
        ...state,
        adminMetrics: { ...state.adminMetrics, isStale: true },
        agentApplications: { ...state.agentApplications, isStale: true },
        activeAgents: { ...state.activeAgents, isStale: true },
        users: { ...state.users, isStale: true },
      };

    case ACTIONS.INVALIDATE_AGENT_CACHE:
      return {
        ...state,
        agentApplications: { ...state.agentApplications, isStale: true },
        activeAgents: { ...state.activeAgents, isStale: true },
      };

    case ACTIONS.INVALIDATE_USER_CACHE:
      return {
        ...state,
        users: { ...state.users, isStale: true },
      };

    default:
      return state;
  }
};

// Context
const AdminDataContext = createContext();

// Cache duration (15 minutes - admin data can be cached longer)
const CACHE_DURATION = 15 * 60 * 1000;

// Provider component
export const AdminDataProvider = ({ children }) => {
  const [state, dispatch] = useReducer(adminDataReducer, initialState);

  // Helper function to check if data is stale
  const isDataStale = useCallback((lastFetched) => {
    if (!lastFetched) return true;
    return Date.now() - lastFetched > CACHE_DURATION;
  }, []);

  // Fetch admin metrics with caching
  const fetchAdminMetrics = useCallback(async (forceRefresh = false) => {
    const { adminMetrics } = state;
    
    if (!forceRefresh && adminMetrics.data && !adminMetrics.isStale && !isDataStale(adminMetrics.lastFetched)) {
      return adminMetrics.data;
    }

    if (adminMetrics.loading) return adminMetrics.data;

    dispatch({ type: ACTIONS.SET_LOADING, dataType: 'adminMetrics', loading: true });

    try {
      const data = await getAdminMetrics();
      dispatch({ type: ACTIONS.SET_ADMIN_METRICS, data });
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch admin metrics';
      dispatch({ type: ACTIONS.SET_ERROR, dataType: 'adminMetrics', error: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  }, [state, isDataStale]);

  const fetchAgentsOverview = useCallback(
    async (forceRefresh = false) => {
      const { agentApplications, activeAgents } = state;

      const applicationsFresh =
        agentApplications.data &&
        !agentApplications.isStale &&
        !isDataStale(agentApplications.lastFetched);
      const activeAgentsFresh =
        activeAgents.data &&
        !activeAgents.isStale &&
        !isDataStale(activeAgents.lastFetched);

      if (!forceRefresh && applicationsFresh && activeAgentsFresh) {
        return {
          applications: agentApplications.data,
          agents: activeAgents.data,
        };
      }

      if (agentApplications.loading || activeAgents.loading) {
        return {
          applications: agentApplications.data,
          agents: activeAgents.data,
        };
      }

      dispatch({ type: ACTIONS.SET_LOADING, dataType: 'agentApplications', loading: true });
      dispatch({ type: ACTIONS.SET_LOADING, dataType: 'activeAgents', loading: true });

      try {
        const [applicationsData, activeAgentsData] = await Promise.all([
          getAgentApplications(),
          getActiveAgents(),
        ]);

        dispatch({ type: ACTIONS.SET_AGENT_APPLICATIONS, data: applicationsData });
        dispatch({ type: ACTIONS.SET_ACTIVE_AGENTS, data: activeAgentsData });

        return {
          applications: applicationsData,
          agents: activeAgentsData,
        };
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Failed to fetch agents information';

        dispatch({
          type: ACTIONS.SET_ERROR,
          dataType: 'agentApplications',
          error: errorMessage,
        });
        dispatch({
          type: ACTIONS.SET_ERROR,
          dataType: 'activeAgents',
          error: errorMessage,
        });

        toast.error(errorMessage);
        throw error;
      }
    },
    [state, isDataStale]
  );

  // Fetch agent applications with caching
  const fetchAgentApplications = useCallback(
    async (forceRefresh = false) => {
      const data = await fetchAgentsOverview(forceRefresh);
      return data.applications;
    },
    [fetchAgentsOverview]
  );

  // Fetch active agents with caching
  const fetchActiveAgents = useCallback(
    async (forceRefresh = false) => {
      const data = await fetchAgentsOverview(forceRefresh);
      return data.agents;
    },
    [fetchAgentsOverview]
  );

  // Fetch users with caching and pagination support
  const fetchUsers = useCallback(async (page = 1, limit = 50, sortOption = 'date', search = '', forceRefresh = false) => {
    const { users } = state;
    const cacheKey = `${page}-${limit}-${sortOption}-${search}`;
    
    // Check if we should use cache (same parameters and not stale)
    const shouldUseCache = !forceRefresh && 
      users.data && 
      !users.isStale && 
      !isDataStale(users.lastFetched) &&
      users.pagination.page === page &&
      users.pagination.limit === limit &&
      users.sortBy === sortOption &&
      users.searchQuery === search;

    if (shouldUseCache) {
      return {
        users: users.data,
        page: users.pagination.page,
        limit: users.pagination.limit,
        totalPages: users.pagination.totalPages,
      };
    }

    if (users.loading) return null;

    dispatch({ type: ACTIONS.SET_LOADING, dataType: 'users', loading: true });

    try {
      const data = await getAllUsers(page, limit, sortOption, search);
      dispatch({ type: ACTIONS.SET_USERS, data, sortBy: sortOption, searchQuery: search });
      return data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch users';
      dispatch({ type: ACTIONS.SET_ERROR, dataType: 'users', error: errorMessage });
      toast.error(errorMessage);
      throw error;
    }
  }, [state, isDataStale]);

  // Cache invalidation methods
  const invalidateAllCache = useCallback(() => {
    dispatch({ type: ACTIONS.INVALIDATE_CACHE });
  }, []);

  const invalidateAgentCache = useCallback(() => {
    dispatch({ type: ACTIONS.INVALIDATE_AGENT_CACHE });
  }, []);

  const invalidateUserCache = useCallback(() => {
    dispatch({ type: ACTIONS.INVALIDATE_USER_CACHE });
  }, []);

  // Method to call when refresh button is clicked in AdminPage
  const onRefreshClicked = useCallback(async () => {
    invalidateAllCache();
    // Fetch fresh admin metrics immediately
    return await fetchAdminMetrics(true);
  }, [invalidateAllCache, fetchAdminMetrics]);

  // Method to call when agent-related actions are performed
  const onAgentActionCompleted = useCallback(() => {
    invalidateAgentCache();
  }, [invalidateAgentCache]);

  // Method to call when user-related actions are performed
  const onUserActionCompleted = useCallback(() => {
    invalidateUserCache();
  }, [invalidateUserCache]);

  const value = {
    // State
    adminMetrics: state.adminMetrics,
    agentApplications: state.agentApplications,
    activeAgents: state.activeAgents,
    users: state.users,
    
    // Fetch methods
    fetchAdminMetrics,
    fetchAgentApplications,
    fetchActiveAgents,
    fetchUsers,
    
    // Cache invalidation
    invalidateAllCache,
    invalidateAgentCache,
    invalidateUserCache,
    
    // Event handlers for cache invalidation
    onRefreshClicked,
    onAgentActionCompleted,
    onUserActionCompleted,
  };

  return (
    <AdminDataContext.Provider value={value}>
      {children}
    </AdminDataContext.Provider>
  );
};

// Hook to use the context
export const useAdminData = () => {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error('useAdminData must be used within an AdminDataProvider');
  }
  return context;
};

export default AdminDataContext; 